import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Row,
  Select,
  Space,
  Tag,
  message,
} from "antd";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../utils/consts";
import { useAuth } from "../../../utils/useAuth";
import { isAdmin, isSuperAdmin } from "../../../utils/permissions";
import { usePermissions } from "../../../utils/usePermissions";
import { Collapse } from "antd";

type PermissionItem = { key: string; desc?: string; page?: string };
type AccessProfile = {
  _id: string;
  name: string;
  permissions: string[];
  isSystem?: boolean;
};

const UserPermissions: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const authUser: any = useAuth();
  const clientId = userData?.clientId?._id || userData?.clientId;
  const { availablePermissions, effectivePermissions: myEffectivePermissions } = usePermissions();
  const [users, setUsers] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [directPermissions, setDirectPermissions] = useState<string[]>([]);
  const [effectivePermissions, setEffectivePermissions] = useState<string[]>(
    []
  );
  const [userProfiles, setUserProfiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState<string[] | undefined>(
    userData?.clientId?.settings?.enabledFeatures
  );

  const canDelegatePermissions =
    (myEffectivePermissions || []).includes("settings:manage-permissions") ||
    (userData?.permissions || []).includes("settings:manage-permissions");

  const canManage =
    isAdmin(authUser?.roles) ||
    isSuperAdmin(authUser?.roles || userData?.roles) ||
    canDelegatePermissions;

  const loadStatic = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const [permsRes, profilesRes, usersRes, featuresRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/admin/${clientId}/permissions`),
        axios.get(`${BASE_API_URL}/admin/${clientId}/access-profiles`),
        axios.get(`${BASE_API_URL}/${clientId}/users`),
        axios.get(`${BASE_API_URL}/client/${clientId}/features`).catch(() => ({
          data: {
            enabledFeatures: userData?.clientId?.settings?.enabledFeatures,
          },
        })),
      ]);
      setPermissions(permsRes.data || []);
      setProfiles(profilesRes.data || []);
      setUsers(usersRes.data || []);
      setEnabledFeatures(
        featuresRes.data?.enabledFeatures ??
          userData?.clientId?.settings?.enabledFeatures
      );
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          t("Failed to load permissions and profiles")
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId: string) => {
    if (!clientId || !userId) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_API_URL}/admin/${clientId}/users/${userId}/permissions`
      );
      setDirectPermissions(res.data?.permissions || []);
      setEffectivePermissions(res.data?.effectivePermissions || []);
      setUserProfiles(
        (res.data?.accessProfiles || []).map((p: any) => p._id || p)
      );
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          t("Failed to load selected user permissions")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatic();
  }, [clientId]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserPermissions(selectedUserId);
    }
  }, [selectedUserId]);

  const saveDirectPermissions = async () => {
    if (!clientId || !selectedUserId) return;
    try {
      setLoading(true);
      await axios.put(
        `${BASE_API_URL}/admin/${clientId}/users/${selectedUserId}/permissions`,
        { permissions: directPermissions }
      );
      message.success(t("Permissions updated"));
      loadUserPermissions(selectedUserId);
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Failed to update permissions")
      );
    } finally {
      setLoading(false);
    }
  };

  const saveProfiles = async () => {
    if (!clientId || !selectedUserId) return;
    try {
      setLoading(true);
      const res = await axios.put(
        `${BASE_API_URL}/admin/${clientId}/users/${selectedUserId}/profiles`,
        { profileIds: userProfiles }
      );
      setDirectPermissions(res.data?.permissions || []);
      setEffectivePermissions(res.data?.effectivePermissions || []);
      message.success(t("Access profiles updated"));
      loadStatic();
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message || t("Failed to update access profiles")
      );
    } finally {
      setLoading(false);
    }
  };

  const permissionOptions = useMemo(() => {
    const list =
      permissions.length > 0
        ? permissions
        : availablePermissions.map((k: string) => ({
            key: k,
            desc: "",
            page: "other",
          }));
    const featureSource =
      enabledFeatures ??
      userData?.clientId?.settings?.enabledFeatures ??
      undefined;
    const pageToFeature: Record<string, string> = {
      dashboard: "dashboard",
      patients: "patients",
      "patient-profile": "patientProfile",
      appointments: "appointments",
      schedules: "schedules",
      shifts: "shifts",
      staff: "staff",
      hierarchy: "hierarchy",
      "user-profile": "userProfile",
      "user-payments": "userProfile",
      "user-leaves": "userProfile",
      "user-overtimes": "userProfile",
      payments: "payments",
      leaves: "leaves",
      overtimes: "overtime",
      business: "business",
      finance: "finance",
      calendars: "calendar",
      support: "support",
      "settings-general": "settings",
      "settings-locations": "settings",
      "settings-working-pattern": "settings",
      "settings-leaves": "settings",
      "settings-overtimes": "settings",
      "settings-work-sectors": "settings",
      "settings-business-categories": "settings",
      "settings-daily-revenue": "settings",
      "settings-company-contracts": "settings",
      "settings-client-features": "settings",
      "settings-super-admin": "settings",
      "settings-permissions": "settings",
      "client-investments": "client-investments",
      investments: "investments",
      apartments: "apartments",
      suppliers: "suppliers",
      credits: "credits",
      reservations: "reservations",
      salaries: "business",
      invoices: "business",
      "own-invoices": "business",
    };

    const filtered = list.filter((p: any) => {
      if (!featureSource || !featureSource.length) return true;
      // Keep already-assigned permissions visible even if the feature isn't enabled
      const isAlreadySelected =
        directPermissions.includes(p.key) || effectivePermissions.includes(p.key);
      if (isAlreadySelected) return true;
      const mappedFeature = pageToFeature[p.page];
      // Stranice koje nisu u mapi nisu vezane za neki feature flag
      // (npr. sistemske/interne stranice) — ne filtriramo ih.
      if (!mappedFeature) return true;
      return featureSource.includes(mappedFeature);
    });

    return filtered.map((p: any) => ({
      label: `${p.key}${p.desc ? ` - ${p.desc}` : ""}`,
      value: p.key,
      page: p.page || "other",
    }));
  }, [
    permissions,
    availablePermissions,
    userData?.roles,
    userData?.clientId?.settings?.enabledFeatures,
    directPermissions,
    effectivePermissions,
  ]);

  const groupedPermissions = useMemo(() => {
    return permissionOptions.reduce(
      (acc: Record<string, { value: string; label: string }[]>, p: any) => {
        const page = p.page || "other";
        if (!acc[page]) acc[page] = [];
        acc[page].push({ value: p.value, label: p.label });
        return acc;
      },
      {}
    );
  }, [permissionOptions]);

  return (
    <Card loading={loading}>
      <Row justify="space-between" align="middle">
        <Col>
          <h3>{t("User Permissions")}</h3>
        </Col>
        <Col>
          <Select
            showSearch
            style={{ minWidth: 220 }}
            placeholder={clientId ? t("Select user") : t("Client not detected")}
            value={selectedUserId}
            onChange={(val) => setSelectedUserId(val)}
            optionFilterProp="label"
            options={users.map((u) => ({
              value: u._id,
              label:
                `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
                u.email ||
                u.username,
            }))}
            disabled={!clientId || !users.length}
          />
        </Col>
      </Row>

      <Divider />

      {!selectedUserId ? (
        <div>{t("Select a user to manage permissions")}</div>
      ) : (
        <>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <h4>{t("Access Profiles")}</h4>
              <Checkbox.Group
                style={{ width: "100%" }}
                value={userProfiles}
                onChange={(vals) => setUserProfiles(vals as string[])}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {profiles.map((p) => (
                    <Checkbox key={p._id} value={p._id}>
                      <Space>
                        <span>{p.name}</span>
                        {p.isSystem && <Tag color="blue">System</Tag>}
                      </Space>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
              <Button
                type="primary"
                block
                style={{ marginTop: 12 }}
                onClick={saveProfiles}
                disabled={!canManage}
              >
                {t("Save Profiles")}
              </Button>
            </Col>
            <Col xs={24} md={12}>
              <h4>{t("Direct Permissions")}</h4>
              {permissionOptions.length ? (
                <Checkbox.Group
                  style={{ width: "100%", maxHeight: 320, overflow: "auto" }}
                  value={directPermissions}
                  onChange={(vals) => setDirectPermissions(vals as string[])}
                >
                  <Collapse
                    defaultActiveKey={Object.keys(groupedPermissions)}
                    style={{ background: "#fff" }}
                  >
                    {Object.entries(groupedPermissions).map(
                      ([page, perms]: any) => (
                        <Collapse.Panel
                          header={<b>{page}</b>}
                          key={page}
                          style={{ background: "#fff" }}
                        >
                          <Space direction="vertical" style={{ width: "100%" }}>
                            {perms.map((p: any) => (
                              <Checkbox value={p.value} key={p.value}>
                                {p.label}
                              </Checkbox>
                            ))}
                          </Space>
                        </Collapse.Panel>
                      )
                    )}
                  </Collapse>
                </Checkbox.Group>
              ) : (
                <div>{t("No permissions available to select")}</div>
              )}
              <Button
                type="primary"
                block
                style={{ marginTop: 12 }}
                onClick={saveDirectPermissions}
                disabled={!canManage}
              >
                {t("Save Permissions")}
              </Button>
            </Col>
          </Row>

          <Divider />

          <h4>{t("Effective Permissions")}</h4>
          <Space wrap>
            {effectivePermissions.map((p) => (
              <Tag key={p}>{p}</Tag>
            ))}
          </Space>
        </>
      )}
    </Card>
  );
};

export default UserPermissions;
